import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getFullName } from '@helpers/userName';
import { useTranslation } from './useTranslation';

export interface AccountPerson {
    id: number;
    firstName: string | null;
    lastName: string | null;
    profileImageUrl: string | null;
    isRemoved?: boolean;
}

interface AccountPeopleState {
    account?: { account?: { people?: AccountPerson[]; users?: AccountPerson[] } };
}

const peopleIndexes = new WeakMap<AccountPerson[], Map<number, AccountPerson>>();
const noPeople = new Map<number, AccountPerson>();

const indexPeople = (people?: AccountPerson[]) => {
    if (!people) {
        return noPeople;
    }

    let index = peopleIndexes.get(people);
    if (!index) {
        index = new Map(people.map(person => [person.id, person]));
        peopleIndexes.set(people, index);
    }
    return index;
};

export const useAccountPeople = () => {
    const { t } = useTranslation();
    const people = useSelector(
        (state: AccountPeopleState) => state.account?.account?.people ?? state.account?.account?.users,
    );
    const peopleById = indexPeople(people);

    const getPerson = useCallback((id?: number | null) => (id == null ? undefined : peopleById.get(id)), [peopleById]);

    const getPersonName = useCallback(
        (id?: number | null, emptySymbol = '') => {
            const person = getPerson(id);
            const name = getFullName(person?.firstName, person?.lastName, emptySymbol);
            const hasName = Boolean(person?.firstName || person?.lastName);
            return person?.isRemoved && hasName ? t('common.removedPersonName', { name }) : name;
        },
        [getPerson, t],
    );

    return { isLoaded: people !== undefined, getPerson, getPersonName };
};
